import type {
  AcceptInviteResponse,
  AgentSettings,
  AIEditPlaybookRequest,
  AIEditPlaybookResponse,
  AskCite,
  AuditCategory,
  ActionLedgerActor,
  ActionLedgerEvent,
  AuditEvent,
  AutomationRule,
  BillingPortalLinkResponse,
  BillingSettings,
  Bookmark,
  BookmarkGraphResponse,
  Brief,
  BriefKind,
  BriefOutcome,
  ListBriefsResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  BusinessContext,
  CadenceSettings,
  ClientAuditEvent,
  ClientAuditEventBatch,
  CreateAutomationRuleRequest,
  CreateBookmarkRequest,
  CreateDecisionRequest,
  CreateInviteRequest,
  CreateInviteResponse,
  CreateIssueRequest,
  CreateMeasurableRequest,
  CreateMeasurementRequest,
  CreateNoteRequest,
  CreatePlaybookRequest,
  Decision,
  DecisionStatus,
  Issue,
  IssueStatus,
  ListDecisionsResponse,
  ListIssuesResponse,
  UpdateDecisionRequest,
  UpdateIssueRequest,
  VoteIssueResponse,
  CreateRockRequest,
  CreateScorecardRequest,
  CompanyContext,
  UpdateCompanyContextRequest,
  UpdateMeRequest,
  GetMeResponse,
  ApprovalRequest,
  AgentAction,
  BulkApprovalRequest,
  EnrichCompanyRequest,
  EnrichCompanyResponse,
  EnrichPersonRequest,
  EnrichPersonResponse,
  LearnedFact,
  PendingDraftsResponse,
  LearnedFactsResponse,
  ListOrgPinnedEntitiesResponse,
  OnboardingGovernancePreferences,
  OnboardingRevealApproveRequest,
  OnboardingRevealLearnedFact,
  OnboardingRevealResponse,
  OnboardingRevealSummary,
  OnboardingSeedStatus,
  OnboardingVoicePreferences,
  UpdateOnboardingGovernancePreferencesRequest,
  UpdateOnboardingVoicePreferencesRequest,
  EntityResolutionCandidate,
  ExternalSignal,
  FinalizeAttachmentRequest,
  GlobalSearchRequest,
  GlobalSearchResponse,
  GovernanceCategory,
  GovernanceItem,
  GovernanceStatus,
  Insight,
  InsightSeverity,
  InsightStatus,
  InternalSignalSeverity,
  InvitePreview,
  L10PrepBrief,
  L10PrepBriefRequest,
  ListAgentActionsResponse,
  ListAskThreadsResponse,
  ListAutomationRulesResponse,
  ListBookmarksResponse,
  ListExternalSignalsResponse,
  ListInsightsResponse,
  ListNotesResponse,
  ListOrgsResponse,
  ListRocksResponse,
  ListSignalsResponse,
  MemoryFeedResponse,
  NearMissesResponse,
  ListTodosResponse,
  CreateTodoRequest,
  Todo,
  UpdateTodoRequest,
  Measurable,
  Measurement,
  MetricDrillResponse,
  Note,
  NoteAttachmentKind,
  Organization,
  OrgInvite,
  OrgMember,
  OrgSettings,
  OrgSubscription,
  PersonalizationProfile,
  Playbook,
  PlaybookDepartment,
  PlaybookScope,
  PlatformUserRow,
  PrepareAttachmentRequest,
  PrepareAttachmentResponse,
  QuickCaptureSuggestRequest,
  QuickCaptureSuggestResponse,
  ReadAskThreadResponse,
  ReadScorecardResponse,
  ResetOnboardingResponse,
  SourceSnapshot,
  VacationMode,
  OrgRole,
  ResolutionConfidenceBand,
  Rock,
  RockStatus,
  Scorecard,
  SecuritySettings,
  UpdateAutomationRuleRequest,
  UpdateBusinessContextRequest,
  UpdateMeasurableRequest,
  UpdateNoteRequest,
  UpdatePlaybookRequest,
  UpdateRockRequest,
  AgentCalibrationSummary,
  AgentChangelogEntry,
  AgentMiss,
  AgentQuestion,
  CitationFeedback,
  CitationFeedbackReason,
  ConflictResolution,
  DeepResearchDepth,
  DeepResearchRun,
  FactConflict,
  FactSource,
  FeedbackEvent,
  FeedbackEventKind,
  FeedbackSentiment,
  FeedbackTargetRef,
} from '@leanopx/shared';
import type {
  ConnectorKind,
  ConnectorSyncLogResponse,
  ConnectorTrustMap,
  ManualMergeRequest,
  ManualSplitRequest,
  Notification,
  PublicOrgConnector,
  UpdateConnectorScopeRequest,
} from '@leanopx/shared';
// Runtime imports — the only Zod schemas the client parses at the boundary
// (the rest cast directly per the comment below). Added for the v7 personal-
// profile learned-facts endpoint where the wire shape is fresh and we want
// the parse to catch drift before it surfaces in the UI as a confusing crash.
import { LearnedFactsResponse as LearnedFactsResponseSchema } from '@leanopx/shared';

/** OrgPreferences wire shape (matches the per-org-preferences-store). */
export interface OrgPreferences {
  orgId: string;
  agent: AgentSettings;
  cadence: CadenceSettings;
  security: SecuritySettings;
  billing: BillingSettings;
  updatedAt: string;
}
import { authHeaders } from './auth-headers';
import { logger } from './logger';
import { ApiError } from './orgs-client';

/**
 * Minimal LeanOpx API client. Phase 1C surface — only the multi-tenant
 * scaffolding endpoints exposed by `apps/functions/src/http/router.ts`
 * (orgs, invites, platform, audit). Phase 4/5 will add EOS + ask/sse.
 *
 * Response shapes are cast directly: the server is in-tree TypeScript,
 * so wire-format drift is caught at compile-time on the server side.
 * Phase 5 can re-introduce Zod parses where wire-format drift across a
 * deploy boundary is a real risk (e.g. share-token receivers).
 */

const API_BASE = '/api';

/**
 * Parse a server error body into the `{ reason, detail }` shape `ApiError`
 * carries. Tolerant of non-JSON bodies — returns `{ reason: null, detail:
 * null }` when JSON.parse fails or the envelope lacks a string `error`
 * field. Mirrors the helper in `orgs-client.ts`; kept in sync so both
 * client modules emit the same typed error shape.
 */
function parseErrorBody(bodyText: string): { reason: string | null; detail: unknown } {
  try {
    const json = JSON.parse(bodyText) as { error?: unknown; detail?: unknown };
    const reason = typeof json.error === 'string' ? json.error : null;
    return { reason, detail: json.detail ?? null };
  } catch {
    return { reason: null, detail: null };
  }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { headers: await authHeaders() });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    logger.warn('api.get failed', {
      path,
      status: res.status,
      body: body.slice(0, 200),
    });
    const { reason, detail } = parseErrorBody(body);
    throw new ApiError({ status: res.status, reason, detail, bodyText: body });
  }
  return (await res.json()) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logger.warn('api.post failed', {
      path,
      status: res.status,
      body: text.slice(0, 200),
    });
    const { reason, detail } = parseErrorBody(text);
    throw new ApiError({ status: res.status, reason, detail, bodyText: text });
  }
  // 204 No Content + empty body — return undefined cast to T.
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function patchJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logger.warn('api.patch failed', {
      path,
      status: res.status,
      body: text.slice(0, 200),
    });
    const { reason, detail } = parseErrorBody(text);
    throw new ApiError({ status: res.status, reason, detail, bodyText: text });
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function putJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logger.warn('api.put failed', {
      path,
      status: res.status,
      body: text.slice(0, 200),
    });
    const { reason, detail } = parseErrorBody(text);
    throw new ApiError({ status: res.status, reason, detail, bodyText: text });
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

async function delJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logger.warn('api.delete failed', {
      path,
      status: res.status,
      body: text.slice(0, 200),
    });
    const { reason, detail } = parseErrorBody(text);
    throw new ApiError({ status: res.status, reason, detail, bodyText: text });
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text().catch(() => '');
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * Discriminated union of SSE events the Ask conversation surface
 * emits. Mirrors the wire-format comment in
 * `apps/functions/src/synthesis/ask.ts` + `packages/shared/src/ask.ts`.
 *
 * - `thread`: server has resolved/created the thread (carries id+title)
 * - `token`:  one streamed text chunk (append to the live message)
 * - `cites`:  citations resolved (replace any prior cites)
 * - `done`:   stream complete (mark the message stable; reload thread list)
 * - `error`:  terminal failure mid-stream
 */
export type AskStreamEvent =
  | { type: 'thread'; threadId: string; title: string }
  | { type: 'token'; text: string }
  | { type: 'cites'; cites: AskCite[] }
  | {
      type: 'done';
      messageId?: string;
      confidence?: number;
      interpretation?: boolean;
    }
  | { type: 'error'; error: string };

/**
 * Open an SSE connection to `POST /api/orgs/:o/ask` and yield each
 * event as it arrives. The handler buffers across chunk boundaries so
 * a multi-chunk SSE frame is never split mid-event. The returned async
 * generator yields when each event lands; the caller drives the loop
 * with `for await (...)`.
 *
 * Aborting: pass an AbortSignal to cancel mid-stream. The fetch read
 * loop respects abort.
 */
export async function* streamAsk(
  orgId: string,
  body: { question: string; threadId?: string },
  signal?: AbortSignal,
): AsyncGenerator<AskStreamEvent, void, void> {
  const res = await fetch(`${API_BASE}/orgs/${encodeURIComponent(orgId)}/ask`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(await authHeaders()),
    },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    logger.warn('api.ask stream failed', {
      status: res.status,
      body: text.slice(0, 200),
    });
    yield { type: 'error', error: `${res.status} ${text || 'no_body'}` };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // SSE frames are delimited by a blank line. We accumulate chunks
  // until we see one, then parse all complete frames out of the buffer.
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let nlIdx: number;
    // Handle both \n\n and \r\n\r\n delimiters.
    while ((nlIdx = findFrameEnd(buffer)) >= 0) {
      const frame = buffer.slice(0, nlIdx);
      buffer = buffer.slice(nlIdx + (buffer[nlIdx] === '\r' ? 4 : 2));
      const evt = parseFrame(frame);
      if (evt) yield evt;
    }
  }

  // Flush the decoder.
  buffer += decoder.decode();
  if (buffer.trim()) {
    const evt = parseFrame(buffer);
    if (evt) yield evt;
  }
}

function findFrameEnd(buf: string): number {
  const a = buf.indexOf('\n\n');
  const b = buf.indexOf('\r\n\r\n');
  if (a < 0) return b;
  if (b < 0) return a;
  return Math.min(a, b);
}

interface ParsedSseFrame {
  event?: string;
  data?: string;
}

function parseFrame(frame: string): AskStreamEvent | null {
  const lines = frame.split(/\r?\n/);
  const parts: ParsedSseFrame = {};
  for (const line of lines) {
    if (!line || line.startsWith(':')) continue; // skip blank lines + comments
    const colon = line.indexOf(':');
    if (colon < 0) continue;
    const field = line.slice(0, colon);
    // SSE spec allows a leading space after the colon.
    let value = line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') parts.event = value;
    else if (field === 'data') parts.data = (parts.data ?? '') + value;
  }
  if (parts.event === 'close') return null;
  if (!parts.data) return null;
  try {
    const json = JSON.parse(parts.data) as Record<string, unknown>;
    const payload = (json['payload'] ?? json) as Record<string, unknown>;
    const t = (parts.event ?? json['type']) as string | undefined;
    if (!t) return null;
    if (t === 'thread') {
      return {
        type: 'thread',
        threadId: String(payload['threadId'] ?? ''),
        title: String(payload['title'] ?? ''),
      };
    }
    if (t === 'token') {
      return { type: 'token', text: String(payload['text'] ?? '') };
    }
    if (t === 'cites') {
      const c = (payload['cites'] as AskCite[] | undefined) ?? [];
      return { type: 'cites', cites: c };
    }
    if (t === 'done') {
      return {
        type: 'done',
        ...(payload['messageId'] ? { messageId: String(payload['messageId']) } : {}),
        ...(typeof payload['confidence'] === 'number'
          ? { confidence: payload['confidence'] as number }
          : {}),
        ...(typeof payload['interpretation'] === 'boolean'
          ? { interpretation: payload['interpretation'] as boolean }
          : {}),
      };
    }
    if (t === 'error') {
      return { type: 'error', error: String(payload['error'] ?? 'unknown') };
    }
    return null;
  } catch {
    return null;
  }
}

function qs(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

export const api = {
  /** Health probe — public, returns `{ ok: true, ts }`. */
  health: () => getJson<{ ok: true; ts: string }>('/health'),

  // Invites
  /** Sanitized invite preview by token (unauth path; server ignores Authorization). */
  previewInvite: (token: string) => getJson<InvitePreview>(`/invites/${encodeURIComponent(token)}`),
  /** Accept an invite as the signed-in user. */
  acceptInvite: (token: string) =>
    postJson<AcceptInviteResponse>(`/invites/${encodeURIComponent(token)}/accept`, {}),

  // Orgs
  /** List orgs the caller belongs to (+ optional primaryOrgId). */
  listOrgs: () => getJson<ListOrgsResponse>('/orgs'),
  /**
   * Create an org. Any authenticated user can create up to
   * `MAX_ORGS_PER_USER` (10) orgs — past the cap the server returns 409
   * `org_limit_reached`. SuperAdmins can override via the platform
   * endpoints. Used by both the self-serve `/onboarding/create-org` flow
   * and the superAdmin `/superadmin/orgs/new` console.
   */
  createOrg: (body: { name: string; slug: string; firstAdminUid?: string }) =>
    postJson<Organization>('/orgs', body),
  /** Read a single org (orgMember). */
  readOrg: (orgId: string) => getJson<Organization>(`/orgs/${encodeURIComponent(orgId)}`),
  /** Patch an org's name/settings (orgAdmin). */
  updateOrg: (orgId: string, body: { name?: string; settings?: Partial<OrgSettings> }) =>
    patchJson<Organization>(`/orgs/${encodeURIComponent(orgId)}`, body),

  // Members
  /** List members of an org (orgMember). */
  listMembers: (orgId: string) =>
    getJson<{ members: OrgMember[] }>(`/orgs/${encodeURIComponent(orgId)}/members`),
  /**
   * Update a member (orgAdmin). Optional `role` triggers a claim refresh
   * + token revoke server-side; optional `team` / `ownerOf` are
   * presentational and don't touch claims. `null` on team/ownerOf
   * explicitly clears the field.
   */
  updateMember: (
    orgId: string,
    uid: string,
    body: {
      role?: OrgRole;
      team?: string | null;
      ownerOf?: string | null;
    },
  ) =>
    patchJson<OrgMember>(
      `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`,
      body,
    ),
  /** Remove a member from the org (orgAdmin). */
  removeMember: (orgId: string, uid: string) =>
    delJson<void>(`/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`),

  // Org invites
  /** Mint an org invite token (orgAdmin). */
  createInvite: (orgId: string, body: CreateInviteRequest) =>
    postJson<CreateInviteResponse>(`/orgs/${encodeURIComponent(orgId)}/invites`, body),
  /** List pending+consumed invites for an org (orgAdmin). */
  listInvites: (orgId: string) =>
    getJson<{ invites: OrgInvite[] }>(`/orgs/${encodeURIComponent(orgId)}/invites`),
  /** Revoke an outstanding invite token (orgAdmin). */
  revokeInvite: (orgId: string, token: string) =>
    delJson<void>(`/orgs/${encodeURIComponent(orgId)}/invites/${encodeURIComponent(token)}`),

  // Platform admin
  /** List all platform users (superAdmin). */
  listPlatformUsers: () => getJson<{ users: PlatformUserRow[] }>('/platform/users'),
  /** Grant superAdmin platform role to a user (superAdmin). */
  grantSuperAdmin: (uid: string) =>
    postJson<void>(`/platform/users/${encodeURIComponent(uid)}/grant-superadmin`, {}),
  /** Revoke superAdmin platform role from a user (superAdmin). */
  revokeSuperAdmin: (uid: string) =>
    postJson<void>(`/platform/users/${encodeURIComponent(uid)}/revoke-superadmin`, {}),
  /** Read the platform-wide audit feed (superAdmin), optionally filtered by org. */
  listPlatformAudit: (params: { orgId?: string; limit?: number } = {}) =>
    getJson<{ events: AuditEvent[] }>(
      `/platform/audit${qs({ orgId: params.orgId, limit: params.limit })}`,
    ),

  // Audit (every signed-in user)
  /** Batch-record client-side audit events (orgMember). */
  recordAuditEventBatch: (events: ClientAuditEvent[]) =>
    postJson<{ ok: true }>('/audit/events', {
      events,
    } satisfies ClientAuditEventBatch),
  /** Read the caller's own user-visible audit feed (orgMember). */
  listMyAudit: (params: { limit?: number } = {}) =>
    getJson<{ events: AuditEvent[] }>(`/audit/me${qs({ limit: params.limit })}`),

  // Me (per-user resource — no org scope; epic #123 Phase 2)
  /**
   * Read the AI-derived "what we learned about you" rows on the
   * personal profile. Returns `{ facts: [] }` when the user has no
   * LinkedIn URL captured on their BusinessContext (the page renders
   * the empty-state). Parsed via Zod at the boundary so wire drift
   * surfaces here, not deep in the renderer.
   */
  getMyLearnedFacts: async (): Promise<LearnedFactsResponse> => {
    const raw = await getJson<unknown>('/me/learned-facts');
    return LearnedFactsResponseSchema.parse(raw);
  },
  /**
   * Submit a free-text correction on a single learned fact. Phase 2
   * records the audit event; backend processing (re-synthesis or
   * persisted override) is a follow-up under epic #123.
   */
  submitFactCorrection: (factId: string, correction: string) =>
    postJson<{ status: 'received' }>(`/me/learned-facts/${encodeURIComponent(factId)}/correct`, {
      correction,
    }),

  /**
   * Read the signed-in user's profile (epic #123 Phase 3). Returns
   * displayName + email from Firebase Auth joined with the told mirror
   * fields from `users/{uid}` (title / timezone / phone / location).
   * The personal-profile page hydrates told rows from this on mount so
   * the patched values round-trip across page reloads.
   */
  getMe: () => getJson<GetMeResponse>('/me'),

  /**
   * Persist a told-row update on the signed-in user's identity (epic
   * #123 Phase 3). Round-trips `displayName` through Firebase Auth and
   * the secondary told fields (`title` / `timezone` / `phone` /
   * `location`) through the `users/{uid}` Firestore mirror.
   */
  updateMe: (body: UpdateMeRequest) => patchJson<{ ok: true; fields: string[] }>('/me', body),

  /**
   * Wipe `BusinessContext.onboardingCompleted` on the active org so the
   * agent-led onboarding wizard re-fires on the next portal load.
   * Audit-logged on the server; the response carries the orgIds that
   * were reset so the client can confirm scope.
   */
  resetOnboarding: () => postJson<ResetOnboardingResponse>('/me/reset-onboarding', {}),

  /**
   * Soft-delete the signed-in user. Sets `users/{uid}.deletedAt`,
   * revokes refresh tokens (signs the user out everywhere), audit-
   * logged. Returns 204 — the client should sign-out immediately and
   * redirect to `/sign-in`.
   */
  deleteMe: () => delJson<void>('/me'),

  /**
   * Read the per-org company profile (epic #123 Phase 3). Returns both
   * the `companyContexts/{orgId}` doc and the web-research learned
   * facts derived from the BusinessContext's `companyUrl`. orgMember
   * gate; defaults synthesized when no doc exists yet (so the page
   * renders without a 404-then-PUT round-trip).
   */
  getCompanyProfile: (orgId: string) =>
    getJson<{ context: CompanyContext; learnedFacts: LearnedFact[] }>(
      `/orgs/${encodeURIComponent(orgId)}/company-profile`,
    ),
  /** Upsert the per-org company profile (orgAdmin). */
  putCompanyProfile: (orgId: string, body: UpdateCompanyContextRequest) =>
    putJson<CompanyContext>(`/orgs/${encodeURIComponent(orgId)}/company-profile`, body),
  /** Learned facts only (separate endpoint for refresh affordances). */
  getCompanyLearnedFacts: (orgId: string) =>
    getJson<LearnedFactsResponse>(
      `/orgs/${encodeURIComponent(orgId)}/company-profile/learned-facts`,
    ),

  // Playbooks — narrative AI-editable operating documents (one company-
  // level + one per leadership area). Reads gated by orgMember; mutations
  // gated by orgAdmin. AI-edit returns a deterministic mock revision
  // until Phase 5 lands real synthesis.
  listPlaybooks: (
    orgId: string,
    params: {
      scope?: PlaybookScope;
      departmentSlug?: PlaybookDepartment;
    } = {},
  ) =>
    getJson<{ playbooks: Playbook[] }>(
      `/orgs/${encodeURIComponent(orgId)}/playbooks${qs({
        scope: params.scope,
        departmentSlug: params.departmentSlug,
      })}`,
    ),
  createPlaybook: (orgId: string, body: CreatePlaybookRequest) =>
    postJson<Playbook>(`/orgs/${encodeURIComponent(orgId)}/playbooks`, body),
  readPlaybook: (orgId: string, id: string) =>
    getJson<Playbook>(`/orgs/${encodeURIComponent(orgId)}/playbooks/${encodeURIComponent(id)}`),
  updatePlaybook: (orgId: string, id: string, body: UpdatePlaybookRequest) =>
    patchJson<Playbook>(
      `/orgs/${encodeURIComponent(orgId)}/playbooks/${encodeURIComponent(id)}`,
      body,
    ),
  deletePlaybook: (orgId: string, id: string) =>
    delJson<void>(`/orgs/${encodeURIComponent(orgId)}/playbooks/${encodeURIComponent(id)}`),
  aiEditPlaybook: (orgId: string, id: string, body: AIEditPlaybookRequest) =>
    postJson<AIEditPlaybookResponse>(
      `/orgs/${encodeURIComponent(orgId)}/playbooks/${encodeURIComponent(id)}/ai-edit`,
      body,
    ),

  // Business context — per-org capture of industry / stage / region /
  // goals / signal topics. Drives nav personalization, playbook
  // tailoring, and the external-signal feed. The PUT route is upsert-
  // shaped so the wizard's confirm step is a single call regardless
  // of whether the doc already exists.
  /** Read the business context for an org (orgMember). 404 if not yet captured. */
  getBusinessContext: (orgId: string) =>
    getJson<BusinessContext>(`/orgs/${encodeURIComponent(orgId)}/business-context`),
  /** Create-or-update the business context for an org (orgAdmin). */
  putBusinessContext: (orgId: string, body: UpdateBusinessContextRequest) =>
    putJson<BusinessContext>(`/orgs/${encodeURIComponent(orgId)}/business-context`, body),
  /** Delete the business context for an org (orgAdmin). */
  deleteBusinessContext: (orgId: string) =>
    delJson<void>(`/orgs/${encodeURIComponent(orgId)}/business-context`),

  // Onboarding: live company enrichment from a homepage URL via a
  // Google-Search-grounded Vertex Gemini call. Per-field confidence +
  // citations let the wizard pre-fill TRUTHFUL guesses and leave the
  // rest blank when the model has no evidence.
  /** Enrich a company by homepage URL. Authenticated; no org required. */
  enrichCompany: (req: EnrichCompanyRequest) =>
    postJson<EnrichCompanyResponse>('/onboarding/enrich-company', req),
  /** Enrich a user by name + email via Gemini-grounded Google Search; optional `linkedinUrl` + `companyName` disambiguate same-name people. */
  enrichPerson: (req: EnrichPersonRequest) =>
    postJson<EnrichPersonResponse>('/onboarding/enrich-person', req),
  /**
   * Read the per-org onboarding-seed-status doc. The wizard's
   * "Warming up your home" interstitial polls this every ~1s while it
   * holds the user, redirecting to /today on `status: complete` or at
   * the 15s ceiling. orgMember-scoped.
   */
  getOnboardingSeedStatus: (orgId: string) =>
    getJson<OnboardingSeedStatus | null>(
      `/orgs/${encodeURIComponent(orgId)}/onboarding/seed-status`,
    ),

  /**
   * Upsert the per-org onboarding voice / cadence / autonomy picks
   * captured on Step 3 of the agent-led wizard. orgAdmin-only on the
   * server. Returns the typed `OnboardingVoicePreferences` mirror so the
   * caller can re-hydrate local state from the canonical timestamped
   * shape. Failure modes (4xx / 5xx / 404 mid-deploy) bubble as
   * `ApiError` — the wizard treats them as non-blocking.
   */
  upsertOnboardingVoice: (
    orgId: string,
    body: UpdateOnboardingVoicePreferencesRequest,
  ): Promise<OnboardingVoicePreferences> =>
    putJson<OnboardingVoicePreferences>(
      `/orgs/${encodeURIComponent(orgId)}/onboarding/voice`,
      body,
    ),

  /**
   * Upsert the per-org onboarding governance facts (entity type / inc
   * state / funding round / fiscal year / EIN / board size / employee
   * states / governance worries / special attention) captured on Step 5
   * of the agent-led wizard. orgAdmin-only on the server. Returns the
   * typed `OnboardingGovernancePreferences` mirror. Failure modes bubble
   * as `ApiError`; the wizard treats them as non-blocking and falls
   * through to the parent shell's business-context submit.
   */
  upsertOnboardingGovernance: (
    orgId: string,
    body: UpdateOnboardingGovernancePreferencesRequest,
  ): Promise<OnboardingGovernancePreferences> =>
    putJson<OnboardingGovernancePreferences>(
      `/orgs/${encodeURIComponent(orgId)}/onboarding/governance`,
      body,
    ),

  /**
   * Read the synthesized "what I learned about your business" payload
   * backing the `/onboarding/reveal` magic-moment page. Server reads
   * BusinessContext + roster + connectors + scorecard measurables and
   * returns a typed `OnboardingRevealResponse` (people / rituals /
   * metrics / vocabulary + per-panel confidences + readStats). Heavy
   * synthesis — page-level `staleTime` is intentionally long (5 min)
   * since the underlying signals don't change between briefings.
   */
  getOnboardingReveal: (orgId: string): Promise<OnboardingRevealResponse> =>
    getJson<OnboardingRevealResponse>(`/orgs/${encodeURIComponent(orgId)}/onboarding/reveal`),

  /**
   * Read the redesigned "magic-moment" reveal payload — the
   * `OnboardingRevealSummary` shape (0..100 confidences, panel-wise
   * people/rituals/metrics slices, vocabulary pairs, read counters).
   * Server short-circuits to the stored doc when one exists; first read
   * synthesizes from the legacy reveal + persists. orgMember-scoped.
   */
  getOnboardingRevealSummary: (orgId: string): Promise<OnboardingRevealSummary> =>
    getJson<OnboardingRevealSummary>(
      `/orgs/${encodeURIComponent(orgId)}/onboarding/reveal-summary`,
    ),

  /**
   * Approve one or more reveal panels — stamps `approvedAt`/`approvedBy`
   * on every learned fact in the approved panel(s) and records an
   * `onboarding.reveal.approve` audit row. orgAdmin-only on the server.
   */
  approveRevealPanels: (
    orgId: string,
    body: OnboardingRevealApproveRequest,
  ): Promise<{ ok: true; panelsApproved: string[]; counts: Record<string, number> }> =>
    postJson<{ ok: true; panelsApproved: string[]; counts: Record<string, number> }>(
      `/orgs/${encodeURIComponent(orgId)}/onboarding/reveal/approve`,
      body,
    ),

  /**
   * Submit a free-text correction to one learned fact. Server stamps
   * `correctedAt`/`correctedTo` + records `onboarding.reveal.fact-corrected`
   * with the previous + new claim in the audit detail. orgAdmin-only.
   */
  correctRevealLearnedFact: (
    orgId: string,
    factId: string,
    correctedTo: string,
  ): Promise<OnboardingRevealLearnedFact> =>
    postJson<OnboardingRevealLearnedFact>(
      `/orgs/${encodeURIComponent(orgId)}/onboarding/reveal/learned-facts/${encodeURIComponent(factId)}/correct`,
      { correctedTo },
    ),

  /**
   * List org-scoped pinned entities (seeded by the onboarding pipeline
   * with the company itself; future surfaces can add manual pins via
   * the server). Distinct from Bookmark which is per-user. The Dossiers
   * page merges this list into the per-user bookmark feed.
   */
  listOrgPinnedEntities: (orgId: string) =>
    getJson<ListOrgPinnedEntitiesResponse>(`/orgs/${encodeURIComponent(orgId)}/pinned-entities`),

  // External signals — industry+region-aware news/event feed driven by
  // BusinessContext.signalTopics. The connector pipeline writes
  // server-side (Phase 5: real news adapters; v0: mock generator);
  // clients only read.
  /** List external signals for an org (orgMember). Recent-first. */
  listExternalSignals: (
    orgId: string,
    params: { topic?: string; limit?: number; since?: string } = {},
  ) =>
    getJson<ListExternalSignalsResponse>(
      `/orgs/${encodeURIComponent(orgId)}/external-signals${qs({
        topic: params.topic,
        limit: params.limit,
        since: params.since,
      })}`,
    ),
  /**
   * Refresh the mock signal feed for an org (orgAdmin). Stand-in for
   * the future Cloud Scheduler-driven real news connector loop.
   * Returns `{ written, reason? }` — `reason` is set when no signals
   * were written (no BusinessContext yet, or no subscribed topics).
   */
  refreshExternalSignals: (orgId: string) =>
    postJson<{
      written: number;
      reason?: 'no-context' | 'no-topics';
      /** Engine that ran the refresh. Real model id (e.g.
       *  `gemini-3-flash-preview`) on success, `fallback (no-llm)` or
       *  `fallback (llm-error)` on fallback. */
      model?: string;
      /** True when the live LLM web-search path ran; false when the
       *  store fell back to the deterministic mock catalog (Vertex
       *  unavailable in this env or every batch failed). Surface to
       *  users so mock signals don't look identical to real ones. */
      llmGenerated?: boolean;
    }>(`/orgs/${encodeURIComponent(orgId)}/external-signals/refresh`, {}),

  // Personalization — rules-based v0 reorders nav, tags scorecard
  // emphasis, and orders the playbook list per the BusinessContext.
  // Phase 5 swaps the rules for an LLM synthesis pass; the wire shape
  // stays the same.
  /** Read the personalization profile for an org (orgMember). */
  getPersonalization: (orgId: string) =>
    getJson<PersonalizationProfile>(`/orgs/${encodeURIComponent(orgId)}/personalization`),

  /**
   * Re-seed the AI-suggested default playbooks with industry-aware
   * variants. Only docs that are still untouched defaults get
   * rewritten; user-edited / AI-refined playbooks stay put. Used
   * after the onboarding wizard captures the BusinessContext.
   */
  reseedPlaybooks: (orgId: string) =>
    postJson<{ rewritten: number; skipped: number }>(
      `/orgs/${encodeURIComponent(orgId)}/playbooks/reseed`,
      {},
    ),

  // Notes — personal multi-media notes (markdown body + image/file/
  // voice-memo attachments). Multi-target: 0..N attached entities,
  // plus body `@`-mentions folded into the same `targetEntityIds`
  // array-contains index.
  /**
   * List the caller's notes for an org. Optional filters:
   *   - entityId: scope to a dossier (notes that mention OR are attached to it)
   *   - standaloneOnly: only personal notes with `targets: []`
   *   - tag: filter by personal tag (mutually exclusive with entityId at index level — see store)
   */
  listNotes: (
    orgId: string,
    opts?: {
      entityId?: string;
      standaloneOnly?: boolean;
      tag?: string;
      limit?: number;
    },
  ) => {
    // Coerce boolean → 'true' string so the qs helper (which only
    // accepts string/number values) can emit it as ?standaloneOnly=true.
    const qsOpts: Record<string, string | number | undefined> = {
      ...(opts?.entityId ? { entityId: opts.entityId } : {}),
      ...(opts?.standaloneOnly ? { standaloneOnly: 'true' } : {}),
      ...(opts?.tag ? { tag: opts.tag } : {}),
      ...(opts?.limit !== undefined ? { limit: opts.limit } : {}),
    };
    return getJson<ListNotesResponse>(`/orgs/${encodeURIComponent(orgId)}/notes${qs(qsOpts)}`);
  },
  /** Read a single note (orgMember, author-only). */
  readNote: (orgId: string, id: string) =>
    getJson<Note>(`/orgs/${encodeURIComponent(orgId)}/notes/${encodeURIComponent(id)}`),
  /** Create a note (orgMember). Author stamped server-side from token. */
  createNote: (orgId: string, body: CreateNoteRequest) =>
    postJson<Note>(`/orgs/${encodeURIComponent(orgId)}/notes`, body),
  /** Patch a note (author-only). */
  updateNote: (orgId: string, id: string, body: UpdateNoteRequest) =>
    patchJson<Note>(`/orgs/${encodeURIComponent(orgId)}/notes/${encodeURIComponent(id)}`, body),
  /** Delete a note (author-only). */
  deleteNote: (orgId: string, id: string) =>
    delJson<void>(`/orgs/${encodeURIComponent(orgId)}/notes/${encodeURIComponent(id)}`),

  /**
   * Quick-capture entity-suggest re-rank. POST a draft snippet → top-5
   * related entities with a 1-line reason per row. Powers the "Agent
   * suggests" callout in the QuickCapture SlideOver; debounced ~300ms
   * client-side as the user types. orgMember-scoped; tight rate-limit
   * (30/min/user) — handle 429 by backing off.
   */
  quickCaptureSuggest: (orgId: string, body: QuickCaptureSuggestRequest) =>
    postJson<QuickCaptureSuggestResponse>(
      `/orgs/${encodeURIComponent(orgId)}/quick-capture/suggest`,
      body,
    ),

  // Decisions register (`/decisions`) — every meaningful call the CEO
  // has made, with rationale + sources + status + confidence + revisit
  // date. Server-mediated: every mutation calls `recordAuditEvent`
  // (decision.create / decision.update / decision.delete).
  /**
   * List the decisions for an org. Optional filters:
   *   - status: ACTIVE / REVISIT / REVERSED / SUPERSEDED
   *   - topic: free-form (server normalizes to lowercase)
   *   - limit: 1..500, default 100. Newest-first by `decidedAt`.
   */
  listDecisions: (
    orgId: string,
    opts?: { status?: DecisionStatus; topic?: string; limit?: number },
  ) =>
    getJson<ListDecisionsResponse>(
      `/orgs/${encodeURIComponent(orgId)}/decisions${qs({
        ...(opts?.status ? { status: opts.status } : {}),
        ...(opts?.topic ? { topic: opts.topic } : {}),
        ...(opts?.limit !== undefined ? { limit: opts.limit } : {}),
      })}`,
    ),
  /** Read a single decision (orgMember). */
  readDecision: (orgId: string, id: string) =>
    getJson<Decision>(`/orgs/${encodeURIComponent(orgId)}/decisions/${encodeURIComponent(id)}`),
  /** Create a decision (orgMember). Decider stamped server-side from token. */
  createDecision: (orgId: string, body: CreateDecisionRequest) =>
    postJson<Decision>(`/orgs/${encodeURIComponent(orgId)}/decisions`, body),
  /** Patch a decision (owner-or-orgAdmin). REVERSED requires `reversedReason`. */
  updateDecision: (orgId: string, id: string, body: UpdateDecisionRequest) =>
    patchJson<Decision>(
      `/orgs/${encodeURIComponent(orgId)}/decisions/${encodeURIComponent(id)}`,
      body,
    ),
  /** Delete a decision (orgAdmin-only — server enforces). */
  deleteDecision: (orgId: string, id: string) =>
    delJson<void>(`/orgs/${encodeURIComponent(orgId)}/decisions/${encodeURIComponent(id)}`),

  // Issues board (`/issues`) — EOS IDS workflow. Server-mediated CRUD
  // on the issues/* collection; every mutation calls `recordAuditEvent`
  // (issue.create / issue.update / issue.delete / issue.vote).
  /**
   * List the issues for an org. Optional filters:
   *   - status: Identified / Discussing / Solved
   *   - ownerUid: per-person filter
   *   - limit: 1..500, default 100. Ordered `updatedAt` desc so votes /
   *     status changes / edits bubble freshly-touched issues to the top.
   */
  listIssues: (orgId: string, opts?: { status?: IssueStatus; ownerUid?: string; limit?: number }) =>
    getJson<ListIssuesResponse>(
      `/orgs/${encodeURIComponent(orgId)}/issues${qs({
        ...(opts?.status ? { status: opts.status } : {}),
        ...(opts?.ownerUid ? { ownerUid: opts.ownerUid } : {}),
        ...(opts?.limit !== undefined ? { limit: opts.limit } : {}),
      })}`,
    ),
  /** Read a single issue (orgMember). */
  readIssue: (orgId: string, id: string) =>
    getJson<Issue>(`/orgs/${encodeURIComponent(orgId)}/issues/${encodeURIComponent(id)}`),
  /** Create an issue (orgMember). Creator + ownerUid + shortId stamped server-side. */
  createIssue: (orgId: string, body: CreateIssueRequest) =>
    postJson<Issue>(`/orgs/${encodeURIComponent(orgId)}/issues`, body),
  /** Patch an issue (owner-or-creator-or-orgAdmin). */
  updateIssue: (orgId: string, id: string, body: UpdateIssueRequest) =>
    patchJson<Issue>(`/orgs/${encodeURIComponent(orgId)}/issues/${encodeURIComponent(id)}`, body),
  /**
   * Toggle the caller's vote on an issue (orgMember). Returns the
   * updated issue + the actor's new vote state so the UI can flip
   * its toggle without a refetch.
   */
  voteIssue: (orgId: string, id: string) =>
    postJson<VoteIssueResponse>(
      `/orgs/${encodeURIComponent(orgId)}/issues/${encodeURIComponent(id)}/vote`,
      {},
    ),
  /** Delete an issue (orgAdmin-only — server enforces). */
  deleteIssue: (orgId: string, id: string) =>
    delJson<void>(`/orgs/${encodeURIComponent(orgId)}/issues/${encodeURIComponent(id)}`),

  // Todos — personal task tracker, owner-private. Promote-from-note
  // creates carry noteId + noteCheckboxId; toggling status to 'done'
  // round-trips back to the source note's checkbox (best-effort).
  /** List the caller's todos for an org. */
  listTodos: (orgId: string, opts?: { limit?: number }) =>
    getJson<ListTodosResponse>(
      `/orgs/${encodeURIComponent(orgId)}/todos${qs({
        ...(opts?.limit !== undefined ? { limit: opts.limit } : {}),
      })}`,
    ),
  /** Create a todo (orgMember, owner-stamped server-side). */
  createTodo: (orgId: string, body: CreateTodoRequest) =>
    postJson<Todo>(`/orgs/${encodeURIComponent(orgId)}/todos`, body),
  /** Patch a todo (owner-only). */
  updateTodo: (orgId: string, id: string, body: UpdateTodoRequest) =>
    patchJson<Todo>(`/orgs/${encodeURIComponent(orgId)}/todos/${encodeURIComponent(id)}`, body),
  /** Delete a todo (owner-only). */
  deleteTodo: (orgId: string, id: string) =>
    delJson<void>(`/orgs/${encodeURIComponent(orgId)}/todos/${encodeURIComponent(id)}`),

  // ── Org preferences (orgAdmin) — Settings hub (epic #16) ─────────
  /** Read the consolidated org preferences (4 sub-sections). orgAdmin. */
  getOrgPreferences: (orgId: string) =>
    getJson<OrgPreferences>(`/orgs/${encodeURIComponent(orgId)}/preferences`),
  /** Replace the agent-behavior section. orgAdmin. */
  putAgentSettings: (orgId: string, body: AgentSettings) =>
    putJson<OrgPreferences>(`/orgs/${encodeURIComponent(orgId)}/preferences/agent`, body),
  /** Replace the cadence section. orgAdmin. */
  putCadenceSettings: (orgId: string, body: CadenceSettings) =>
    putJson<OrgPreferences>(`/orgs/${encodeURIComponent(orgId)}/preferences/cadence`, body),
  /** Replace the security section. orgAdmin. */
  putSecuritySettings: (orgId: string, body: SecuritySettings) =>
    putJson<OrgPreferences>(`/orgs/${encodeURIComponent(orgId)}/preferences/security`, body),
  /** Replace the billing section. orgAdmin. */
  putBillingSettings: (orgId: string, body: BillingSettings) =>
    putJson<OrgPreferences>(`/orgs/${encodeURIComponent(orgId)}/preferences/billing`, body),

  // ── Stripe billing (epic #12) ─────────────────────────────────────
  /**
   * Read the per-org Stripe subscription mirror. Returns the
   * `OrgSubscription` doc at the top-level `subscriptions/{orgId}` path.
   * Stripe is the source of truth; this endpoint surfaces just enough
   * (plan / status / period bounds / seats / trial end) for the
   * `/admin/billing` surface to render without round-tripping to Stripe
   * on every page load. orgAdmin.
   */
  readBillingSubscription: (orgId: string) =>
    getJson<OrgSubscription>(`/orgs/${encodeURIComponent(orgId)}/billing/subscription`),
  /**
   * Start a Stripe Checkout session for a self-serve plan switch
   * (operator / team — enterprise is sales-led). Returns a Stripe-hosted
   * URL that the caller redirects the browser to via
   * `window.location.href`. orgAdmin.
   */
  createBillingCheckout: (orgId: string, body: CheckoutSessionRequest) =>
    postJson<CheckoutSessionResponse>(`/orgs/${encodeURIComponent(orgId)}/billing/checkout`, body),
  /**
   * Mint a Stripe Billing Portal link for the org's existing customer
   * (update payment method, download invoices, cancel). 409 when the
   * org has no Stripe customer on file yet. orgAdmin.
   */
  createBillingPortalLink: (orgId: string) =>
    postJson<BillingPortalLinkResponse>(`/orgs/${encodeURIComponent(orgId)}/billing/portal`, {}),
  /**
   * Replace the per-connector trust map (lives under `agent.connectorTrust`).
   * Used by the `/admin/connectors/trust` page's slider-release autosave
   * so two admins editing different agent sub-fields don't clobber each
   * other (the prior `PUT /preferences/agent` round-tripped the whole
   * section). orgAdmin. Returns the merged preferences doc so the
   * caller can prime react-query without a follow-up GET.
   */
  patchConnectorTrust: (orgId: string, trust: ConnectorTrustMap) =>
    patchJson<OrgPreferences>(`/orgs/${encodeURIComponent(orgId)}/preferences/connector-trust`, {
      trust,
    }),

  // ── Per-org audit feed (orgAdmin) (epic #16) ─────────────────────
  /**
   * Per-org audit feed for the /admin/audit page. orgAdmin-only — the
   * platform-wide superAdmin view lives at `/platform/audit`. Optional
   * `category` query filters by AuditCategory. Returns the canonical
   * {@link AuditEvent} shape from `@leanopx/shared` — the page renders
   * a presentation-only mapper on top, never widens with `as unknown`.
   */
  listOrgAudit: (
    orgId: string,
    opts?: {
      category?: AuditCategory;
      limit?: number;
    },
  ) =>
    getJson<{ events: AuditEvent[] }>(`/orgs/${encodeURIComponent(orgId)}/audit${qs(opts ?? {})}`),

  // ── Bookmarks (epic #15) ────────────────────────────────────────
  /** List the caller's bookmarks for an org. Per-user, per-org. */
  listBookmarks: (orgId: string) =>
    getJson<ListBookmarksResponse>(`/orgs/${encodeURIComponent(orgId)}/bookmarks`),
  /** Create a bookmark. Idempotent — duplicate refId returns the existing doc. */
  createBookmark: (orgId: string, body: CreateBookmarkRequest) =>
    postJson<Bookmark>(`/orgs/${encodeURIComponent(orgId)}/bookmarks`, body),
  /** Delete a bookmark by composite id (`{userId}_{orgId}_{refId}`). */
  deleteBookmark: (orgId: string, id: string) =>
    delJson<void>(`/orgs/${encodeURIComponent(orgId)}/bookmarks/${encodeURIComponent(id)}`),
  /**
   * Search the caller's bookmarks by name prefix — backs the
   * `@`-mention picker in the note editor. Returns up to `limit`
   * bookmarks (default 8) ranked by exact > prefix > word > substring.
   * Optional `kinds` filter narrows by BookmarkType.
   */
  searchEntities: (
    orgId: string,
    opts: { q: string; kinds?: Array<Bookmark['type']>; limit?: number },
  ) =>
    getJson<{ bookmarks: Bookmark[] }>(
      `/orgs/${encodeURIComponent(orgId)}/entities/search${qs({
        q: opts.q,
        ...(opts.kinds && opts.kinds.length > 0 ? { kinds: opts.kinds.join(',') } : {}),
        ...(opts.limit !== undefined ? { limit: opts.limit } : {}),
      })}`,
    ),
  /**
   * Knowledge-graph derived from bookmarks. Until epic #9 (Neo4j) lands
   * the server returns synthetic edges from tag co-occurrence + per-type
   * cluster connectors; the wire shape stays stable so the UI doesn't
   * change when the real graph backend takes over.
   */
  getBookmarkGraph: (orgId: string) =>
    getJson<BookmarkGraphResponse>(`/orgs/${encodeURIComponent(orgId)}/bookmarks/graph`),
  /**
   * Prepare an attachment upload. Returns a signed PUT URL the client
   * uses to upload directly to Cloud Storage; after the upload, call
   * `finalizeNoteAttachment` with `{kind, filename, mimeType, storagePath}`
   * to persist the attachment record on the note.
   */
  prepareNoteAttachment: (orgId: string, noteId: string, body: PrepareAttachmentRequest) =>
    postJson<PrepareAttachmentResponse>(
      `/orgs/${encodeURIComponent(orgId)}/notes/${encodeURIComponent(noteId)}/attachments/prepare`,
      body,
    ),
  /**
   * Finalize an attachment upload after the Storage PUT completes.
   * For voice-memo attachments, the server fires a background
   * Gemini transcription pass and patches `transcript` onto the
   * attachment record; the client polls the parent note to pick it up.
   */
  finalizeNoteAttachment: (
    orgId: string,
    noteId: string,
    attachmentId: string,
    body: FinalizeAttachmentRequest & {
      kind: NoteAttachmentKind;
      filename: string;
      mimeType: string;
      storagePath: string;
    },
  ) =>
    postJson<Note>(
      `/orgs/${encodeURIComponent(orgId)}/notes/${encodeURIComponent(noteId)}/attachments/${encodeURIComponent(attachmentId)}/finalize`,
      body,
    ),

  // Ask — reactive synthesis (epic #10). The streaming POST lives at
  // `streamAsk` (above) because it returns an async generator; the
  // thread-list + thread-read endpoints are conventional JSON.
  /** List the caller's Ask threads for an org (orgMember). */
  listAskThreads: (orgId: string, limit?: number) =>
    getJson<ListAskThreadsResponse>(
      `/orgs/${encodeURIComponent(orgId)}/ask/threads${qs({ limit })}`,
    ),
  /** Read a single Ask thread + its messages (orgMember, owner-only). */
  readAskThread: (orgId: string, id: string) =>
    getJson<ReadAskThreadResponse>(
      `/orgs/${encodeURIComponent(orgId)}/ask/threads/${encodeURIComponent(id)}`,
    ),

  // L10 prep brief (epic #10). One-shot Vertex Gemini call returning
  // the 5-section pre-meeting outline. Falls back to a deterministic
  // mock when ADC is unbound; the wire shape is identical either way.
  /** Generate a Sunday-night-ready L10 prep brief (orgMember). */
  generateL10PrepBrief: (orgId: string, body: L10PrepBriefRequest = {}) =>
    postJson<L10PrepBrief>(`/orgs/${encodeURIComponent(orgId)}/l10/prep-brief`, body),

  // Global search — full-page `/search` surface backend. Synthesizes a
  // cited agent paragraph from the org's insights / notes / dossiers /
  // agent actions / decisions when at least 3 candidates match the
  // query; falls through to raw rows otherwise. Multi-tenant via the
  // URL `orgId`; the route enforces `assertOrgScope` so a misaligned
  // active-org id 403s.
  /** POST a free-text search against the org's graph (orgMember). */
  globalSearch: (orgId: string, body: GlobalSearchRequest): Promise<GlobalSearchResponse> =>
    postJson<GlobalSearchResponse>(`/orgs/${encodeURIComponent(orgId)}/search`, body),

  // Entity resolution (epic #9). Admin queue for mid-band Person merge
  // candidates the scorer surfaced; high-band auto-applies, low-band stays
  // separate. Migration runner kicks off idempotent Cypher CREATE
  // CONSTRAINT/INDEX statements.
  /** List ER candidates filtered by band (orgAdmin). */
  listErCandidates: (orgId: string, filter: { band?: ResolutionConfidenceBand } = {}) =>
    getJson<{ candidates: EntityResolutionCandidate[] }>(
      `/orgs/${encodeURIComponent(orgId)}/graph/resolution/candidates${qs({
        ...(filter.band ? { band: filter.band } : {}),
      })}`,
    ),
  /** Approve an ER candidate, applying the merge (orgAdmin). */
  approveErCandidate: (orgId: string, candidateId: string) =>
    postJson<{ ok: true; decisionId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/graph/resolution/${encodeURIComponent(
        candidateId,
      )}/approve`,
      {},
    ),
  /** Reject an ER candidate, marking the pair as kept-separate (orgAdmin). */
  rejectErCandidate: (orgId: string, candidateId: string) =>
    postJson<{ ok: true; decisionId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/graph/resolution/${encodeURIComponent(
        candidateId,
      )}/reject`,
      {},
    ),
  /** Run the idempotent Cypher migrations for the graph vocabulary (orgAdmin). */
  runGraphMigration: (orgId: string) =>
    postJson<{ applied: number }>(`/orgs/${encodeURIComponent(orgId)}/graph/migrate`, {}),

  // Rocks — quarterly 90-day priorities. Per-leader columns + status +
  // milestones + agent-update body. Read by orgMember; create requires
  // owner-self OR orgAdmin; update requires owner OR orgAdmin; delete
  // requires orgAdmin. Server stamps `pct` from `milestones`.
  /** List rocks for an org. Optional `quarter` / `ownerUid` / `status` filters. */
  listRocks: (
    orgId: string,
    opts?: {
      quarter?: string;
      ownerUid?: string;
      /** Single status, or array (sent as comma-separated; server splits + validates). */
      status?: RockStatus | RockStatus[];
      limit?: number;
    },
  ) => {
    const status = Array.isArray(opts?.status) ? opts!.status.join(',') : opts?.status;
    return getJson<ListRocksResponse>(
      `/orgs/${encodeURIComponent(orgId)}/rocks${qs({
        quarter: opts?.quarter,
        ownerUid: opts?.ownerUid,
        status,
        limit: opts?.limit,
      })}`,
    );
  },
  /** Create a Rock. Non-admins can only own their own; admins assign on behalf. */
  createRock: (orgId: string, body: CreateRockRequest) =>
    postJson<Rock>(`/orgs/${encodeURIComponent(orgId)}/rocks`, body),
  /** Patch a Rock (owner or orgAdmin). */
  updateRock: (orgId: string, id: string, body: UpdateRockRequest) =>
    patchJson<Rock>(`/orgs/${encodeURIComponent(orgId)}/rocks/${encodeURIComponent(id)}`, body),
  /** Delete a Rock (orgAdmin). */
  deleteRock: (orgId: string, id: string) =>
    delJson<void>(`/orgs/${encodeURIComponent(orgId)}/rocks/${encodeURIComponent(id)}`),
  /**
   * Trigger agent-update synthesis for a Rock — fires Gemini with the
   * rock + recent connector activity and writes to `rock.agentUpdate`.
   * Currently returns 501 until the connector pipeline lands (epic
   * #7-#10); the route signature is stable so consumers can code
   * against it now.
   *
   * Wire contract: `postJson` throws on every non-2xx, so callers will
   * receive a thrown `Error('501 …')` until the pipeline lands. The
   * resolved-value type only carries the success shape that callers
   * can actually observe; the deferred case is a thrown error pattern,
   * not a typed alternative.
   */
  syncRockUpdate: (orgId: string, id: string) =>
    postJson<{ ok: true }>(
      `/orgs/${encodeURIComponent(orgId)}/rocks/${encodeURIComponent(id)}/sync-update`,
      {},
    ),

  // Scorecard — weekly measurables. Reads gated by orgMember; structural
  // mutations (create scorecard, create/update measurable) gated by
  // orgAdmin; recording a weekly value is open to any orgMember (the
  // owner reports their own row, but the integrator can backstop).
  // The read endpoint computes `delta` + `spark` + `status` server-side
  // so the UI doesn't re-derive goal arithmetic.
  /** Read a scorecard with derived rows + recent measurements (orgMember). */
  readScorecard: (orgId: string, scorecardId: string, opts: { weeks?: number } = {}) =>
    getJson<ReadScorecardResponse>(
      `/orgs/${encodeURIComponent(orgId)}/scorecards/${encodeURIComponent(scorecardId)}${qs({
        weeks: opts.weeks,
      })}`,
    ),
  /** Create a new scorecard (orgAdmin). The seeder + UI default to one per org. */
  createScorecard: (orgId: string, body: CreateScorecardRequest) =>
    postJson<Scorecard>(`/orgs/${encodeURIComponent(orgId)}/scorecards`, body),
  /** Append a measurable row (orgAdmin). Body's `scorecardId` must match the URL. */
  createMeasurable: (orgId: string, scorecardId: string, body: CreateMeasurableRequest) =>
    postJson<Measurable>(
      `/orgs/${encodeURIComponent(orgId)}/scorecards/${encodeURIComponent(scorecardId)}/measurables`,
      body,
    ),
  /** Patch a measurable row (orgAdmin). */
  updateMeasurable: (
    orgId: string,
    scorecardId: string,
    measurableId: string,
    body: UpdateMeasurableRequest,
  ) =>
    patchJson<Measurable>(
      `/orgs/${encodeURIComponent(orgId)}/scorecards/${encodeURIComponent(scorecardId)}/measurables/${encodeURIComponent(measurableId)}`,
      body,
    ),
  /** Record a weekly measurement for a row (orgMember; capturedByUid stamped server-side). */
  recordMeasurement: (orgId: string, scorecardId: string, body: CreateMeasurementRequest) =>
    postJson<Measurement>(
      `/orgs/${encodeURIComponent(orgId)}/scorecards/${encodeURIComponent(scorecardId)}/measurements`,
      body,
    ),
  /**
   * Read the per-measurable drill response — history (13 weeks) + compare
   * deltas (w/w · m/m · q/q · y/y) + drill rows (empty until a connector
   * pipeline is wired). Powers `/scorecard/:measurableId` (orgMember).
   */
  getMetricDrill: (orgId: string, measurableId: string) =>
    getJson<MetricDrillResponse>(
      `/orgs/${encodeURIComponent(orgId)}/measurables/${encodeURIComponent(measurableId)}/drill`,
    ),

  // Entity resolution — manual merge/split (epic #9, orgAdmin).
  /** Manually merge two Persons into one (orgAdmin). */
  manualErMerge: (orgId: string, body: ManualMergeRequest) =>
    postJson<{ ok: true; decisionId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/graph/resolution/merge`,
      body,
    ),
  /** Manually split identities off a Person into a new one (orgAdmin). */
  manualErSplit: (orgId: string, body: ManualSplitRequest) =>
    postJson<{ ok: true; decisionId: string; newPersonId: string }>(
      `/orgs/${encodeURIComponent(orgId)}/graph/resolution/split`,
      body,
    ),

  // Connectors (epics #7 + #8). Admin install + manage flow for the
  // marketplace connectors (Slack, Gmail, Salesforce, GitHub, Stripe).
  /** List the org's connectors (orgAdmin). */
  listConnectors: (orgId: string) =>
    getJson<{ connectors: PublicOrgConnector[] }>(`/orgs/${encodeURIComponent(orgId)}/connectors`),
  /**
   * Start the OAuth install dance. Returns the vendor's auth URL the
   * caller redirects the admin to. The callback hits
   * `/api/connectors/:kind/callback` which redirects back to
   * `/admin/connectors?connected=<kind>` on success.
   */
  startConnectorInstall: (orgId: string, kind: ConnectorKind, body: { memberUid?: string } = {}) =>
    postJson<{ kind: ConnectorKind; authUrl: string; state: string }>(
      `/orgs/${encodeURIComponent(orgId)}/connectors/${encodeURIComponent(kind)}/install`,
      body,
    ),
  /** Stripe-only manual install — admin pastes restricted key + webhook secret. */
  installStripeManual: (orgId: string, body: { apiKey: string; webhookSecret: string }) =>
    postJson<{ ok: true }>(
      `/orgs/${encodeURIComponent(orgId)}/connectors/stripe/install-manual`,
      body,
    ),
  /** Trigger a backfill for the named connector (orgAdmin). */
  triggerConnectorBackfill: (orgId: string, kind: ConnectorKind) =>
    postJson<{ ok: true; outcome: string }>(
      `/orgs/${encodeURIComponent(orgId)}/connectors/${encodeURIComponent(kind)}/backfill`,
      {},
    ),
  /** Update a connector's scope (e.g. Slack channel selection) (orgAdmin). */
  updateConnectorScope: (orgId: string, kind: ConnectorKind, body: UpdateConnectorScopeRequest) =>
    patchJson<{ ok: true }>(
      `/orgs/${encodeURIComponent(orgId)}/connectors/${encodeURIComponent(kind)}/scope`,
      body,
    ),
  /** Unsubscribe + pause a connector (orgAdmin). */
  unsubscribeConnector: (orgId: string, kind: ConnectorKind) =>
    postJson<{ ok: true }>(
      `/orgs/${encodeURIComponent(orgId)}/connectors/${encodeURIComponent(kind)}/unsubscribe`,
      {},
    ),
  /**
   * Per-connector sync log — rolling activity feed of webhook events
   * + Pub/Sub pushes processed for this connector. TTL-bounded
   * (~30 days). Returns the latest N events + rollup counts so the
   * admin connector detail page can show "Slack: 47 messages, 3
   * channel events, 0 failures in the last window".
   */
  getConnectorSyncLog: (
    orgId: string,
    kind: ConnectorKind,
    params: { limit?: number; memberUid?: string } = {},
  ) =>
    getJson<ConnectorSyncLogResponse>(
      `/orgs/${encodeURIComponent(orgId)}/connectors/${encodeURIComponent(kind)}/sync-log${qs({
        limit: params.limit,
        memberUid: params.memberUid,
      })}`,
    ),

  // ── Proactive insights (epic #11) ──────────────────────────────────
  // Today briefing + Insights/Signals/Alerts feeds + Agent log. Every
  // route is read-only (orgMember) except the headline-refresh POST and
  // the per-insight dismiss POST. Server-only writes for everything else
  // — the synthesis pipeline owns the data.

  /**
   * Brief-as-Dossier API (issue #166 PR B). One doc per generation pass,
   * IMMUTABLE after delivery — corrections + dispositions live on the
   * action ledger (per #166 Phase 1 §06 Q3).
   *
   * Owner scope is implicit — the server reads `auth.uid` from the
   * token. Pass `teamOnly: true` to surface team-scoped briefs
   * (`owner_user_id IS NULL`) instead of the caller's own. There is
   * no client-side knob to request another user's briefs: per-user
   * briefs are private to the recipient by design, and the route
   * rejects arbitrary `ownerUid` values with 403 anyway.
   */
  briefs: {
    /** Archive list with optional kind/unreadOnly/since/limit filters. */
    list: (
      orgId: string,
      params: {
        kind?: BriefKind;
        unreadOnly?: boolean;
        since?: string;
        limit?: number;
        /** Surface team briefs (`owner_user_id IS NULL`) instead of
         *  the caller's own per-user briefs. */
        teamOnly?: boolean;
      } = {},
    ) =>
      getJson<ListBriefsResponse>(
        `/orgs/${encodeURIComponent(orgId)}/briefs${qs({
          ownerUid: params.teamOnly ? 'null' : undefined,
          kind: params.kind,
          unreadOnly: params.unreadOnly ? 'true' : undefined,
          since: params.since,
          limit: params.limit,
        })}`,
      ),
    /**
     * Resolve the caller's latest daily Brief. Returns `{ brief_id }` on
     * hit OR a 404 with `{ kind: 'no-brief-today' }` when no brief has
     * been generated yet today (per spec §06 Q4 — explicit empty state,
     * not auto-jump to yesterday).
     */
    today: (orgId: string) =>
      getJson<{ brief_id: string }>(`/orgs/${encodeURIComponent(orgId)}/briefs/today`),
    /** Read one full Brief doc. */
    get: (orgId: string, briefId: string) =>
      getJson<Brief>(`/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(briefId)}`),
    /** List the action-ledger events for a brief (optional actor/since filters). */
    listActionLedger: (
      orgId: string,
      briefId: string,
      params: { actor?: ActionLedgerActor; since?: string } = {},
    ) =>
      getJson<{ events: ActionLedgerEvent[] }>(
        `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(briefId)}/action-ledger${qs(
          { actor: params.actor, since: params.since },
        )}`,
      ),
    /** List background-observed outcomes for items in a brief. */
    listOutcomes: (orgId: string, briefId: string) =>
      getJson<{ outcomes: BriefOutcome[] }>(
        `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(briefId)}/outcomes`,
      ),
    /**
     * Read the SourceSnapshot the brief was generated from. Drives the
     * "what the agent read" tab (reproducibility contract).
     */
    getSourceSnapshot: (orgId: string, briefId: string) =>
      getJson<SourceSnapshot>(
        `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(briefId)}/source-snapshot`,
      ),
    item: {
      /**
       * Approve the agent's recommendation on an item. v1: ledger-only
       * (downstream tool dispatch wires up in a follow-up). Audited as
       * `brief.item.approve`.
       */
      approve: (
        orgId: string,
        briefId: string,
        itemId: string,
        payload: Record<string, unknown> = {},
      ) =>
        postJson<{ ok: true; action: string; briefId: string; itemId: string }>(
          `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(
            briefId,
          )}/items/${encodeURIComponent(itemId)}/approve`,
          payload,
        ),
      /** Decline with an optional reason. Audited as `brief.item.decline`. */
      decline: (orgId: string, briefId: string, itemId: string, reason?: string) =>
        postJson<{ ok: true; action: string; briefId: string; itemId: string }>(
          `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(
            briefId,
          )}/items/${encodeURIComponent(itemId)}/decline`,
          reason ? { reason } : {},
        ),
      /** Defer to the supplied ISO datetime. Audited as `brief.item.defer`. */
      defer: (orgId: string, briefId: string, itemId: string, until: string) =>
        postJson<{ ok: true; action: string; briefId: string; itemId: string }>(
          `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(
            briefId,
          )}/items/${encodeURIComponent(itemId)}/defer`,
          { until },
        ),
      /** Drill-in (open the source / synthesis trace). Audited as `brief.item.drill`. */
      drill: (orgId: string, briefId: string, itemId: string) =>
        postJson<{ ok: true; action: string; briefId: string; itemId: string }>(
          `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(
            briefId,
          )}/items/${encodeURIComponent(itemId)}/drill`,
          {},
        ),
      /** Pin a note to the item. Audited as `brief.item.pin-note`. */
      pinNote: (orgId: string, briefId: string, itemId: string, noteId: string) =>
        postJson<{ ok: true; action: string; briefId: string; itemId: string }>(
          `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(
            briefId,
          )}/items/${encodeURIComponent(itemId)}/pin-note`,
          { noteId },
        ),
      /**
       * Tell the agent it got something wrong (wires FB-06). The payload
       * is free-form (`{original, correction, propagate?}`) — the agent
       * persists it on the ledger for later learning. Audited as
       * `brief.item.correct`.
       */
      correct: (
        orgId: string,
        briefId: string,
        itemId: string,
        payload: { original?: string; correction?: string; propagate?: boolean },
      ) =>
        postJson<{ ok: true; action: string; briefId: string; itemId: string }>(
          `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(
            briefId,
          )}/items/${encodeURIComponent(itemId)}/correct`,
          payload,
        ),
    },
    /** Mint a public share link. Audited as `brief.share.create`. */
    createShare: (orgId: string, briefId: string) =>
      postJson<{ share_link: string }>(
        `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(briefId)}/share`,
        {},
      ),
    /** Revoke the share link. Audited as `brief.share.revoke`. */
    revokeShare: (orgId: string, briefId: string) =>
      delJson<{ ok: true; briefId: string; share_link: null }>(
        `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(briefId)}/share`,
      ),
    /** Add a comment (team brief, or own-note on per-user brief). Audited as `brief.comment`. */
    comment: (orgId: string, briefId: string, body: string) =>
      postJson<{ ok: true; briefId: string }>(
        `/orgs/${encodeURIComponent(orgId)}/briefs/${encodeURIComponent(briefId)}/comment`,
        { body },
      ),
  },
  /**
   * Per-user, per-org vacation mode (#166 Phase 6 — time & cadence).
   * Server derives `userId` from the verified token; clients can't read
   * or write another user's vacation state.
   */
  vacationMode: {
    get: (orgId: string) =>
      getJson<VacationMode>(`/orgs/${encodeURIComponent(orgId)}/user/vacation-mode`),
    set: (orgId: string, payload: Partial<VacationMode>) =>
      putJson<VacationMode>(`/orgs/${encodeURIComponent(orgId)}/user/vacation-mode`, payload),
  },
  /**
   * List insights. `status` defaults to `new` + `open` server-side when
   * omitted. When `date` is supplied, the response splices the briefing's
   * pinned hero insights into the front so the Today page composition is
   * a single round-trip.
   */
  listInsights: (
    orgId: string,
    params: {
      date?: string;
      sev?: InsightSeverity;
      status?: InsightStatus;
    } = {},
  ) =>
    getJson<ListInsightsResponse>(
      `/orgs/${encodeURIComponent(orgId)}/insights${qs({
        date: params.date,
        sev: params.sev,
        status: params.status,
      })}`,
    ),
  /** Dismiss an insight on behalf of the caller (orgMember). */
  dismissInsight: (orgId: string, id: string) =>
    postJson<Insight>(
      `/orgs/${encodeURIComponent(orgId)}/insights/${encodeURIComponent(id)}/dismiss`,
      {},
    ),
  /** List governance items (v5). Server-side filters by category + status. */
  listGovernance: (
    orgId: string,
    params: { category?: GovernanceCategory; status?: GovernanceStatus } = {},
  ) =>
    getJson<{ items: GovernanceItem[] }>(
      `/orgs/${encodeURIComponent(orgId)}/governance${qs({
        category: params.category,
        status: params.status,
      })}`,
    ),
  /** Read a single governance item. */
  readGovernanceItem: (orgId: string, id: string) =>
    getJson<GovernanceItem>(
      `/orgs/${encodeURIComponent(orgId)}/governance/${encodeURIComponent(id)}`,
    ),
  /** Move a governance item to a new status (acknowledge / snooze / done). */
  setGovernanceStatus: (orgId: string, id: string, status: GovernanceStatus) =>
    postJson<GovernanceItem>(
      `/orgs/${encodeURIComponent(orgId)}/governance/${encodeURIComponent(id)}/status`,
      { status },
    ),
  /** List internal (graph-derived) signals. Distinct from external signals. */
  listSignals: (orgId: string, params: { since?: string; sev?: InternalSignalSeverity } = {}) =>
    getJson<ListSignalsResponse>(
      `/orgs/${encodeURIComponent(orgId)}/signals${qs({
        since: params.since,
        sev: params.sev,
      })}`,
    ),
  /**
   * List alerts — same shape as `/signals` but pre-filtered to severity
   * ∈ {watch, critical}. The `sev=critical` query narrows further.
   */
  listAlerts: (orgId: string, params: { since?: string; sev?: 'critical' } = {}) =>
    getJson<ListSignalsResponse>(
      `/orgs/${encodeURIComponent(orgId)}/alerts${qs({
        since: params.since,
        sev: params.sev,
      })}`,
    ),
  /** List recent agent actions (the `/agent` log). */
  listAgentActions: (orgId: string, params: { day?: string; since?: string } = {}) =>
    getJson<ListAgentActionsResponse>(
      `/orgs/${encodeURIComponent(orgId)}/agent/actions${qs({
        day: params.day,
        since: params.since,
      })}`,
    ),
  /**
   * Memory feed — server categorizes audit events into the closed set of
   * `MemoryEventKind` rows for `/memory`. Window defaults to 7 days
   * server-side; pass `since`/`until` to scope a different range.
   * Returns `{events, tally, window}` per `MemoryFeedResponse`.
   */
  getMemoryFeed: (orgId: string, params: { since?: string; until?: string } = {}) =>
    getJson<MemoryFeedResponse>(
      `/orgs/${encodeURIComponent(orgId)}/memory/feed${qs({
        since: params.since,
        until: params.until,
      })}`,
    ),
  /**
   * Near-misses — heuristic catch attribution for `/memory/near-misses`.
   * Returns `{hero?, past30Days, tally}` per `NearMissesResponse`. The
   * window defaults to 30 days server-side; pass `since` to override.
   */
  getNearMisses: (orgId: string, params: { since?: string } = {}) =>
    getJson<NearMissesResponse>(
      `/orgs/${encodeURIComponent(orgId)}/memory/near-misses${qs({
        since: params.since,
      })}`,
    ),

  // ── Approvals queue (Phase 4 / Epic #123) ────────────────────────────
  /**
   * List drafts the agent is currently holding for CEO sign-off. Returns
   * the wire `PendingDraftsResponse`; the surface page (`/approvals`) and
   * the sidebar badge both consume this.
   */
  listApprovals: (orgId: string) =>
    getJson<PendingDraftsResponse>(`/orgs/${encodeURIComponent(orgId)}/approvals`),
  /**
   * Record the CEO's decision on a single draft. Server-side flips the
   * underlying `agentActions/*` row's status to `completed` (approve/edit)
   * or `rejected`, and stamps `decision` + `decidedBy` + `decidedAt`.
   */
  decideApproval: (orgId: string, draftId: string, body: ApprovalRequest) =>
    postJson<{ action: AgentAction }>(
      `/orgs/${encodeURIComponent(orgId)}/approvals/${encodeURIComponent(draftId)}`,
      body,
    ),
  /**
   * Bulk-approve up to 50 routine drafts in one call. The server emits a
   * per-row audit event (so the operator can see in `/admin/audit` which
   * drafts were bulk-approved together) and returns a `{ results }` array
   * with one entry per requested draftId.
   */
  bulkApprove: (orgId: string, draftIds: string[]) =>
    postJson<{ results: Array<{ draftId: string; ok: boolean; reason?: string }> }>(
      `/orgs/${encodeURIComponent(orgId)}/approvals/bulk-approve`,
      { draftIds } satisfies BulkApprovalRequest,
    ),

  // ── Notifications (issue #155) ───────────────────────────────────────
  /**
   * List the signed-in user's notifications within an org. Server
   * gates by `userId == auth.uid` so a teammate's bell never surfaces
   * here. Most-recent-first; default 50, hard-capped at 200 server-side.
   * The bell-dropdown calls this with `limit=5`; the `/notifications`
   * inbox page uses the default.
   */
  listNotifications: (orgId: string, opts?: { limit?: number }) =>
    getJson<{ notifications: Notification[] }>(
      `/orgs/${encodeURIComponent(orgId)}/notifications${
        opts?.limit ? `?limit=${opts.limit}` : ''
      }`,
    ),
  /**
   * Flip a single notification's `read` flag to `true`. The server
   * mirrors the firestore.rules constraint and rejects anything other
   * than `{read: true}` with a 400. Idempotent — re-marking a row
   * already read is a no-op.
   */
  markNotificationRead: (orgId: string, id: string) =>
    patchJson<{ notification: Notification }>(
      `/orgs/${encodeURIComponent(orgId)}/notifications/${encodeURIComponent(id)}`,
      { read: true },
    ),

  // ── Automation rules (Phase 5 / Epic #123) ───────────────────────────
  // Operator-defined "When X → Then Y" entries grouped by category on
  // `/rules`. All four endpoints are orgAdmin-only (the surface itself is
  // admin-gated in the router + sidebar).
  /** List all automation rules for an org. Returned ordered by `createdAt` ASC. */
  listAutomationRules: (orgId: string) =>
    getJson<ListAutomationRulesResponse>(`/orgs/${encodeURIComponent(orgId)}/automation-rules`),
  /** Create a new automation rule (orgAdmin). Server stamps id + timestamps + createdByUid. */
  createAutomationRule: (orgId: string, body: CreateAutomationRuleRequest) =>
    postJson<AutomationRule>(`/orgs/${encodeURIComponent(orgId)}/automation-rules`, body),
  /**
   * Patch an automation rule (orgAdmin). Empty body is permitted (the
   * server still bumps `updatedAt`); pass `{ enabled }` for a toggle-only
   * update or `{ name }` for a rename-only update.
   */
  updateAutomationRule: (orgId: string, ruleId: string, body: UpdateAutomationRuleRequest) =>
    patchJson<AutomationRule>(
      `/orgs/${encodeURIComponent(orgId)}/automation-rules/${encodeURIComponent(ruleId)}`,
      body,
    ),
  /** Delete an automation rule (orgAdmin). Returns `{ ok: true }` on success. */
  deleteAutomationRule: (orgId: string, ruleId: string) =>
    delJson<{ ok: true }>(
      `/orgs/${encodeURIComponent(orgId)}/automation-rules/${encodeURIComponent(ruleId)}`,
    ),

  // ── Feedback / Agent / Dossier conflicts / Source weights / Ask extras
  // ── (issue #166 PR A — Trust Loop) ──────────────────────────────────
  // The micro-feedback ledger, the agent's "9 things I want to ask you"
  // queue, the calibration page, the per-org changelog, the dossier
  // conflict resolver, the per-user source-weight tuning page, and the
  // Ask deep-research / citation-feedback affordances all share the
  // same access posture: orgMember, server-stamped actorUid + orgId,
  // server-side audit emission on mutations.

  /** Record a single micro-feedback event (FB-01 … FB-06). */
  recordFeedback: (
    orgId: string,
    payload: {
      kind: FeedbackEventKind;
      target: FeedbackTargetRef;
      sentiment: FeedbackSentiment;
      body?: string;
    },
  ) => postJson<FeedbackEvent>(`/orgs/${encodeURIComponent(orgId)}/feedback`, payload),

  /** List the caller's open (or resolved) agent-question queue. */
  listAgentQuestions: (orgId: string, opts?: { resolved?: boolean }) =>
    getJson<{ questions: AgentQuestion[] }>(
      `/orgs/${encodeURIComponent(orgId)}/agent/questions${qs({
        resolved: opts?.resolved !== undefined ? String(opts.resolved) : undefined,
      })}`,
    ),

  /** Resolve a single agent question with an answer. */
  resolveAgentQuestion: (orgId: string, id: string, answer: string) =>
    postJson<AgentQuestion>(
      `/orgs/${encodeURIComponent(orgId)}/agent/questions/${encodeURIComponent(id)}/resolve`,
      { answer },
    ),

  /** Calibration summary + recent misses for the agent calibration page. */
  getAgentCalibration: (orgId: string) =>
    getJson<{ misses: AgentMiss[]; summary: AgentCalibrationSummary }>(
      `/orgs/${encodeURIComponent(orgId)}/agent/calibration`,
    ),

  /** "What the agent learned this week" feed. Optional `sinceTs` for incremental. */
  listAgentChangelog: (orgId: string, opts?: { sinceTs?: string }) =>
    getJson<{ entries: AgentChangelogEntry[] }>(
      `/orgs/${encodeURIComponent(orgId)}/agent/changelog${qs({ sinceTs: opts?.sinceTs })}`,
    ),

  /** List fact conflicts for a dossier. `openOnly: true` filters to unresolved. */
  listDossierConflicts: (orgId: string, dossierId: string, opts?: { openOnly?: boolean }) =>
    getJson<{ conflicts: FactConflict[] }>(
      `/orgs/${encodeURIComponent(orgId)}/dossier/${encodeURIComponent(dossierId)}/conflicts${qs({
        openOnly: opts?.openOnly ? 'true' : undefined,
      })}`,
    ),

  /** Resolve a fact conflict with the CEO's pick/override + optional source policy. */
  resolveDossierConflict: (
    orgId: string,
    dossierId: string,
    id: string,
    resolution: Pick<ConflictResolution, 'kind' | 'value' | 'sourcePolicy'>,
  ) =>
    postJson<FactConflict>(
      `/orgs/${encodeURIComponent(orgId)}/dossier/${encodeURIComponent(
        dossierId,
      )}/conflict/${encodeURIComponent(id)}/resolve`,
      resolution,
    ),

  /** Read the caller's per-source weight map (defaults overlaid with overrides). */
  getSourceWeights: (orgId: string) =>
    getJson<{
      weights: Record<FactSource, { default: number; current: number; locked: boolean }>;
    }>(`/orgs/${encodeURIComponent(orgId)}/sources/weights`),

  /**
   * Patch per-source weights. The server rejects writes to locked
   * sources (gcal / voice-notes / told) with HTTP 400 `weight_locked`
   * — the entire patch fails when any locked source is touched.
   */
  setSourceWeights: (orgId: string, weights: Partial<Record<FactSource, number>>) =>
    patchJson<{
      weights: Record<FactSource, { default: number; current: number; locked: boolean }>;
    }>(`/orgs/${encodeURIComponent(orgId)}/sources/weights`, weights),

  /** Kick off a deep-research run on Ask. Returns the freshly-minted run + initial step. */
  createDeepResearchRun: (orgId: string, question: string, depth: DeepResearchDepth) =>
    postJson<DeepResearchRun>(`/orgs/${encodeURIComponent(orgId)}/ask/deep-research`, {
      question,
      depth,
    }),

  /**
   * Record per-citation feedback. The optional `userSuggestion` is the
   * free-text "cite this instead" the CEO can type alongside the
   * canned reason.
   */
  recordCitationFeedback: (
    orgId: string,
    citationId: string,
    payload: { reason: CitationFeedbackReason; userSuggestion?: string },
  ) =>
    postJson<CitationFeedback>(
      `/orgs/${encodeURIComponent(orgId)}/citations/${encodeURIComponent(citationId)}/feedback`,
      payload,
    ),
};

// Re-export from the canonical shared schema so existing
// `import { PersonalizationProfile } from '@/lib/api'` call sites keep
// working without an import rewrite. The Zod schema lives at
// `packages/shared/src/personalization.ts`.
export type {
  BriefingHighlight,
  BriefingHighlightKind,
  PersonalizationProfile,
} from '@leanopx/shared';

/** Re-export the ExternalSignal shape for surfaces that consume the list. */
export type { ExternalSignal };

/**
 * Re-export the approvals-queue shapes for the `/approvals` surface +
 * its sub-components (the row, the slide-over editor).
 */
export type {
  ApprovalDecision,
  ApprovalRequest,
  BulkApprovalRequest,
  DraftApprovalStatus,
  DraftChannel,
  DraftRoutineness,
  PendingDraft,
  PendingDraftsResponse,
} from '@leanopx/shared';

/** Re-export the Ask + L10 prep shapes consumed by the /ask + /l10 surfaces. */
export type {
  AskCite,
  AskCiteKind,
  AskMessage,
  AskMessageRole,
  AskRequest,
  AskThread,
  L10PrepBrief,
  L10PrepBriefRequest,
  L10PrepBullet,
  L10PrepSections,
  L10PrepSeverity,
  ListAskThreadsResponse,
  ReadAskThreadResponse,
} from '@leanopx/shared';

/** Re-export the global-search wire shapes for the /search surface. */
export type {
  GlobalSearchCitation,
  GlobalSearchRequest,
  GlobalSearchResponse,
  GlobalSearchResult,
  GlobalSearchResultKind,
  GlobalSearchTypeFilter,
  GlobalSearchTimeFilter,
  GlobalSearchConfidenceFilter,
} from '@leanopx/shared';

/** Re-export the onboarding-reveal wire shapes for the page wiring. */
export type {
  OnboardingRevealResponse,
  RevealPerson as RevealPersonWire,
  RevealRitual as RevealRitualWire,
  RevealMetric as RevealMetricWire,
  RevealVocabularyPair,
  RevealReadStats,
  RevealMetricTrend,
  RevealRitualCadence,
} from '@leanopx/shared';

/** Re-export the redesigned reveal-summary shapes (row-03 magic moment). */
export type {
  OnboardingRevealApproveRequest,
  OnboardingRevealLearnedFact,
  OnboardingRevealMetricItem,
  OnboardingRevealPanelKind,
  OnboardingRevealPeopleMember,
  OnboardingRevealRitualItem,
  OnboardingRevealSource,
  OnboardingRevealSummary,
} from '@leanopx/shared';
